import { m as motion } from "framer-motion";
import {
  ArrowRight,
  Award,
  Calendar,
  CheckCircle2,
  Clock,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { CallToActionSection } from "@/components/call-to-action-section";
import { EXTERNAL_LINKS } from "@/lib/constants";
import {
  COHORT_DELIVERABLES,
  EARLY_BIRD_DISCOUNT_CODE,
  EARLY_BIRD_DISCOUNT_LABEL,
  NEXT_COHORT,
  NEXT_COHORT_PAGE_TITLE,
  NEXT_COHORT_PATH,
  NEXT_COHORT_TITLE,
} from "@/lib/cohorts";
import { defaultViewport } from "@/lib/motion";
import { breadcrumb } from "@/lib/seo-schemas";
import { useSEO } from "@/lib/use-seo";

const NextCohortPage = () => {
  useSEO({
    title: NEXT_COHORT_PAGE_TITLE,
    description: NEXT_COHORT.seoDescription,
    path: NEXT_COHORT_PATH,
    ogImage: NEXT_COHORT.ogImage,
    jsonLd: breadcrumb([
      { name: "Home", path: "/" },
      { name: NEXT_COHORT_TITLE, path: NEXT_COHORT_PATH },
    ]),
  });

  return (
    <div className="min-h-screen">
      <section className="relative overflow-hidden bg-gradient-mesh pt-36 md:pt-32 pb-8">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-radial-primary opacity-30 blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-gradient-radial-secondary opacity-30 blur-3xl" />
        </div>
        <div className="section-inner relative z-10 text-center space-y-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold"
          >
            <Calendar className="w-4 h-4" />
            Next cohort starts {NEXT_COHORT.startDateLabel} · Limited Seats
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold"
          >
            {NEXT_COHORT.monthYearLabel}{" "}
            <span className="bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))] bg-clip-text text-transparent">
              Live Cohort
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-foreground/80 max-w-2xl mx-auto"
          >
            An AI engineer and behavioral scientist guiding your HR and Talent
            leaders through a live cohort-based intensive - AI impact in 90
            days.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-6 text-sm text-foreground/70"
          >
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-primary" /> {NEXT_COHORT.timeLabel}{" "}
              · {NEXT_COHORT.weekdayLabel}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Users className="w-4 h-4 text-primary" /> Small cohort format
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Award className="w-4 h-4 text-primary" /> Live virtual
              (recorded)
            </span>
          </motion.div>
        </div>
      </section>

      <section className="section-inner py-10 md:py-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={defaultViewport}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            <span className="bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))] bg-clip-text text-transparent">
              5-Week Schedule
            </span>
          </h2>
          <p className="text-foreground/70 max-w-xl mx-auto">
            Each Thursday, join live for a focused, hands-on session that builds
            on the last.
          </p>
        </motion.div>

        <div className="relative max-w-3xl mx-auto">
          <div className="absolute left-6 md:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))] opacity-30" />

          <div className="space-y-4">
            {NEXT_COHORT.schedule.map((session, i) => (
              <motion.div
                key={session.number}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={defaultViewport}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="relative pl-16 md:pl-20"
              >
                <div className="absolute left-4 md:left-6 top-4 w-4 h-4 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--secondary))] ring-4 ring-background z-10" />

                <div className="glass-card p-5 md:p-6 hover-lift">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary bg-primary/10 px-2.5 py-1 rounded-md">
                        Session {session.number}
                      </span>
                      <span className="text-sm font-medium text-foreground/60 inline-flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {session.date} · {NEXT_COHORT.timeLabel}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold mt-2">{session.title}</h3>
                  <p className="text-foreground/70 text-sm mt-1">
                    {session.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={defaultViewport}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="relative pl-16 md:pl-20 mt-4"
          >
            <div className="absolute left-4 md:left-6 top-4 w-4 h-4 rounded-full border-2 border-dashed border-foreground/30 z-10" />
            <div className="border border-dashed border-foreground/20 rounded-2xl p-5 md:p-6 text-center text-foreground/50">
              <p className="text-sm font-medium">
                Weekly Clinics - dates coming soon
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-foreground/5 py-10 md:py-14">
        <div className="section-inner">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={defaultViewport}
            className="text-3xl md:text-4xl font-bold text-center mb-6"
          >
            What You'll{" "}
            <span className="bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))] bg-clip-text text-transparent">
              Walk Away With
            </span>
          </motion.h2>
          <div className="max-w-2xl mx-auto space-y-4">
            {COHORT_DELIVERABLES.map((item, i) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={defaultViewport}
                transition={{ delay: i * 0.08 }}
                className="flex items-start gap-3"
              >
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span className="text-foreground/80">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-inner py-10 md:py-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={defaultViewport}
          className="glass-card p-8 md:p-10 max-w-3xl mx-auto space-y-6"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-center">
            What If I{" "}
            <span className="bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))] bg-clip-text text-transparent">
              Miss a Session?
            </span>
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Every session is recorded</p>
                <p className="text-sm text-foreground/70">
                  Can&apos;t make a live session? No problem - full recordings
                  are available so you can catch up on your own schedule.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">
                  Retake the entire cohort - free, forever
                </p>
                <p className="text-sm text-foreground/70">
                  With your lifetime membership, you can join this and every
                  future cohort. If life gets in the way, simply jump into the
                  next one in 6 months.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <section className="section-inner py-10 md:py-14">
        <motion.blockquote
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={defaultViewport}
          className="glass-card p-8 md:p-10 max-w-3xl mx-auto text-center"
        >
          <p className="text-lg md:text-xl italic text-foreground/80 leading-relaxed">
            &quot;This program gave me the confidence and the actual output to
            walk into our CHRO&apos;s office and say, &apos;here&apos;s what
            we&apos;re building, here&apos;s why it works, and here&apos;s how
            we scale it.&apos; We shipped our prototype within three weeks of
            the cohort ending.&quot;
          </p>
          <footer className="mt-4 text-sm font-semibold text-primary">
            - Sonja Song, Senior HR Business Partner, Airbnb
          </footer>
        </motion.blockquote>
      </section>

      <section className="bg-foreground/5 py-10">
        <div className="section-inner">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={defaultViewport}
            className="glass-card p-8 max-w-xl mx-auto text-center space-y-4"
          >
            <div className="inline-flex flex-wrap items-center justify-center gap-2 rounded-full bg-secondary/10 px-4 py-2 text-sm font-semibold text-secondary">
              <span>{EARLY_BIRD_DISCOUNT_LABEL}</span>
              <span className="rounded-md bg-background px-2 py-0.5 font-bold text-foreground">
                {EARLY_BIRD_DISCOUNT_CODE}
              </span>
            </div>
            <p className="text-4xl font-bold bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] bg-clip-text text-transparent">
              $1,400
            </p>
            <p className="text-sm text-foreground/60">
              Billed once - Lifetime Access + Live Cohorts
            </p>
            <a
              href={EXTERNAL_LINKS.PREMIUM_MASTERMIND_APPLICATION}
              className="group inline-flex items-center gap-2 px-6 py-3 text-base font-semibold rounded-2xl bg-primary text-primary-foreground shadow-primary hover:shadow-glow-primary transition-all duration-300 hover:-translate-y-1"
            >
              Apply Now
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </a>
            <div>
              <Link
                to="/membership"
                className="text-sm text-foreground/60 hover:text-primary transition-colors underline underline-offset-4"
              >
                Compare all membership tiers →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <CallToActionSection />
    </div>
  );
};

export default NextCohortPage;
